// src/pages/DeliveryCharges.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Delete, History, PauseCircle, AttachMoney } from "@mui/icons-material";
import { URL } from "../constants/url";
import colors from "../constants/colors";

const DeliveryCharges = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newCharge, setNewCharge] = useState("");
  const [reason, setReason] = useState("");
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchCurrent = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${URL}/api/delivery-charge/current`);
      const data = await res.json();
      if (data.success) {
        setCurrent({
          currentCharge: data.deliveryCharge,
          lastUpdated: data.lastUpdated,
        });
      } else {
        // fallback: try admin route to get full doc
        const alt = await fetch(`${URL}/api/admin/delivery-charge`);
        const j = await alt.json();
        if (j.success)
          setCurrent({
            currentCharge: j.data?.currentCharge,
            lastUpdated: j.data?.lastUpdated,
          });
      }
    } catch (err) {
      console.error("fetchCurrent", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${URL}/api/admin/delivery-charge/history`, {
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (data.success) setHistory(data.history || []);
    } catch (err) {
      console.error("fetchHistory", err);
    }
  };

  useEffect(() => {
    fetchCurrent();
    fetchHistory();
  }, []);

  const handleUpdate = async () => {
    if (newCharge === "" || isNaN(Number(newCharge)))
      return alert("Enter valid charge");
    setSaving(true);
    try {
      const body = {
        currentCharge: Number(newCharge),
        reason: reason || "Updated from admin UI",
      };
      const res = await fetch(`${URL}/api/admin/delivery-charge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        setNewCharge("");
        setReason("");
        await fetchCurrent();
        await fetchHistory();
      } else {
        alert(data.message || "Update failed");
      }
    } catch (err) {
      console.error("update", err);
      alert("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteHistory = async (id) => {
    if (!window.confirm("Delete this history entry?")) return;
    try {
      const res = await fetch(
        `${URL}/api/admin/delivery-charge/history/${id}`,
        {
          method: "DELETE",
        }
      );
      const data = await res.json();
      if (data.success) fetchHistory();
      else alert(data.message || "Delete failed");
    } catch (err) {
      console.error("deleteHistory", err);
    }
  };

  const handleDeactivate = async () => {
    if (!window.confirm("Deactivate delivery charges?")) return;
    try {
      const res = await fetch(`${URL}/api/admin/delivery-charge/deactivate`, {
        method: "PATCH",
      });
      const data = await res.json();
      if (data.success) fetchCurrent();
      else alert(data.message || "Operation failed");
    } catch (err) {
      console.error("deactivate", err);
    }
  };

  return (
    <Box sx={{
        height: "100vh", 
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* AppBar */}
      <AppBar
        position="sticky"
        sx={{
          bgcolor: "white",
          color: colors.darkBlack,
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          mb: 2,
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            gap: 2,
            alignItems: "center",
            minHeight: "64px !important",
            px: { xs: 2, sm: 3 },
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Tabs
              value={0}
              sx={{
                minHeight: "48px",
                "& .MuiTabs-indicator": {
                  backgroundColor: colors.green,
                  height: 3,
                  borderRadius: 3,
                },
                "& .MuiTab-root": {
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                  minWidth: "auto",
                  px: { xs: 1, sm: 3 },
                  minHeight: "48px",
                  whiteSpace: "nowrap",
                },
                "& .MuiTab-root.Mui-selected": {
                  color: colors.green,
                },
              }}
            >
              <Tab
                icon={<AttachMoney sx={{ mr: 1, fontSize: { xs: "18px", sm: "20px" } }} />}
                iconPosition="start"
                label={isMobile ? "Delivery" : "Delivery Charges"}
              />
            </Tabs>
          </Box>

          <Chip
            label={`₹${current?.currentCharge ?? "0"}`}
            sx={{
              backgroundColor: colors.lightGreen,
              color: colors.darkGreen,
              fontWeight: 600,
            }}
          />
        </Toolbar>
      </AppBar>

      {/* Original Content - No changes */}
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Delivery Charge Manager
        </Typography>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1">Current Charge</Typography>
            {loading ? (
              <CircularProgress />
            ) : (
              <Typography variant="h6" sx={{ mb: 1 }}>
                {current?.currentCharge ?? "Not set"}
              </Typography>
            )}

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 2 }}
            >
              Last updated:{" "}
              {current?.lastUpdated
                ? new Date(current.lastUpdated).toLocaleString()
                : "—"}
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ alignItems: "center" }}
            >
              <TextField
                label="New charge"
                size="small"
                value={newCharge}
                onChange={(e) => setNewCharge(e.target.value)}
              />
              <TextField
                label="Reason (optional)"
                size="small"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <Button
                variant="contained"
                onClick={handleUpdate}
                disabled={saving}
              >
                {saving ? <CircularProgress size={18} /> : "Update"}
              </Button>
              <Button
                variant="outlined"
                color="warning"
                onClick={handleDeactivate}
              >
                Deactivate
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Change History
            </Typography>

            {history.length === 0 ? (
              <Typography>No history found</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Charge</TableCell>
                    <TableCell>Effective From</TableCell>
                    <TableCell>Changed At</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((h) => (
                    <TableRow key={h._id || h.changedAt}>
                      <TableCell>{h.charge}</TableCell>
                      <TableCell>
                        {h.effectiveFrom
                          ? new Date(h.effectiveFrom).toLocaleString()
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {h.changedAt
                          ? new Date(h.changedAt).toLocaleString()
                          : "—"}
                      </TableCell>
                      <TableCell>{h.reason || "—"}</TableCell>
                      <TableCell>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteHistory(h._id)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default DeliveryCharges;